"use client";

import React from "react";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, Clock, Droplets, Flame, Gauge } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const diagnosticCards = [{
  title: "Over-Drying & Degradation",
  risk: "Thermal Stress & Yield Loss",
  icon: Flame,
  description: "Extended heating cycles drive product temperature past critical thresholds, causing active ingredient thermal degradation, discoloration, and out-of-specification batches.",
  impact: "Compromised batch potency and increased scrap rate."
}, {
  title: "Under-Drying & Instability",
  risk: "Microbial & Shelf-Life Risk",
  icon: Droplets,
  description: "Premature cycle termination leaves residual moisture hidden in the cake or powder bed, triggering caking, microbial proliferation, and stability test failures.",
  impact: "Downstream granulation clogging and stability rejections."
}, {
  title: "Time & Energy Waste",
  risk: "Process Inefficiency",
  icon: Clock,
  description: "Conservative, static cycle recipes inflate furnace and freeze-dryer operational hours by up to 35%, consuming excessive utility capacity and bottlenecking production suites.",
  impact: "Inflated carbon footprint and reduced suite capacity."
}, {
  title: "Sampling Lag & Representativeness",
  risk: "Offline Analytical Latency",
  icon: Activity,
  description: "Manual thief sampling introduces atmospheric moisture, breaks sterile barriers, and incurs multi-hour QC lab wait times while drying vessels idle.",
  impact: "Blind process holding and contamination exposure."
}];
export default function Challenges() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="diagnosticchallenges" data-nav="light" className="bg-background py-20 lg:py-28 text-foreground border-b border-border/60">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          
          {/* Left Column: 4-col context & narrative */}
          <div className="lg:col-span-4 flex flex-col items-start gap-6 lg:sticky lg:top-24">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10 gap-1.5 px-3 py-1 font-semibold">
              <AlertTriangle className="size-3.5 text-primary" />
              Process Vulnerabilities
            </Badge>

            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default leading-tight">
                Critical Drying Pitfalls in Regulated Manufacturing
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                Relying on guesswork or fixed-time drying profiles in regulated pharmaceutical and chemical suites risks batch integrity, inflates utility spend, and creates continuous compliance exposure.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-secondary/80 border border-border w-full space-y-2">
              <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                <Gauge className="size-4 text-primary shrink-0" />
                <span>The PAT Resolution</span>
              </div>
              <p className="text-xs text-muted-foreground leading-normal">
                Real-time inline moisture and endpoint tracking removes offline delays and brings validated accuracy to every batch cycle.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/SolutionsPage">
                  Explore PAT Solutions
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  Consult a Specialist
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: 8-col 2x2 diagnostic cards grid */}
          <motion.div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }}>
            {diagnosticCards.map((card, i) => {
            const IconComponent = card.icon;
            return <div key={card.title} data-index={i} className="h-full">
                  <motion.div variants={itemVariants} className="h-full">
                    <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/50 transition-colors shadow-xs rounded-xl overflow-hidden group">
                      <div className="h-1.5 w-full bg-primary/20 group-hover:bg-primary transition-colors" />
                      
                      <CardHeader className="pt-6 pb-3">
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                            <IconComponent className="size-5" />
                          </div>
                          <span className="text-xs font-semibold text-primary uppercase tracking-wider bg-primary/10 px-2.5 py-1 rounded-md">
                            {card.risk}
                          </span>
                        </div>
                        <CardTitle className="text-xl font-bold text-foreground font-default leading-snug">
                          {card.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-4 pt-1 flex-1 flex flex-col justify-between">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {card.description}
                        </p>
                        
                        <div className="pt-3 border-t border-border/70 flex items-start gap-2">
                          <span className="text-xs font-semibold text-foreground shrink-0 mt-0.5">Impact:</span>
                          <span className="text-xs text-muted-foreground leading-tight">
                            {card.impact}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </motion.div>

        </div>
      </div>
    </section>;
}